import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    CircularProgress,
    Alert,
    Button,
    Pagination,
    Switch,
    FormControlLabel,
    Chip,
    Tabs,
    Tab,
    Box,
    Stack,
    Snackbar
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import styled from "styled-components";
import dayjs from "dayjs";
import { functions } from "../services/firebase";
import { captureError } from "../lib/errorReporting";
import { useIsMobile } from "../hooks/useIsMobile";
import { THEME_MAP } from "../theme";

const PAGE_SIZE = 25;

const PageContainer = styled.div`
    padding: 24px;
    max-width: 1100px;
    margin: 0 auto;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    svg {
        color: ${THEME_MAP["color-primary-500"]};
    }
`;

const Subtle = styled(Typography)`
    color: ${THEME_MAP["color-basic-600"]};
`;

const statusColor = (status) => {
    switch (status) {
        case "sent":
            return "success";
        case "failed":
            return "error";
        case "test":
            return "info";
        case "suppressed":
            return "warning";
        default:
            return "default";
    }
};

function a11yProps(index) {
    return { id: `notification-tab-${index}`, "aria-controls": `notification-tabpanel-${index}` };
}

function TabPanel({ children, value, index }) {
    if (value !== index) return null;
    return (
        <Box role="tabpanel" sx={{ pt: 3 }} {...a11yProps(index)}>
            {children}
        </Box>
    );
}

function stringifyData(value) {
    if (!value || typeof value !== "object") return "{}";
    try {
        return JSON.stringify(value, null, 2);
    } catch (_err) {
        return "{}";
    }
}

export function NotificationSystem() {
    const getSystem = useMemo(() => functions.httpsCallable("getUserNotificationSystem"), []);
    const updateRule = useMemo(() => functions.httpsCallable("updateUserNotificationRule"), []);
    const updateTemplate = useMemo(
        () => functions.httpsCallable("updateUserNotificationTemplate"),
        []
    );
    const listDispatches = useMemo(
        () => functions.httpsCallable("listUserNotificationDispatches"),
        []
    );
    const sendTest = useMemo(() => functions.httpsCallable("sendUserNotificationTest"), []);
    const triggerProximity = useMemo(
        () => functions.httpsCallable("triggerUserProximityNotifications"),
        []
    );
    const isMobile = useIsMobile();

    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [toast, setToast] = useState("");

    const [rules, setRules] = useState([]);
    const [templates, setTemplates] = useState([]);

    // Proximity rule config drafts keyed by slug (controlled, for the live hint).
    const [ruleConfigs, setRuleConfigs] = useState({});
    const [runningProximity, setRunningProximity] = useState(false);

    // Templates editing state keyed by slug.
    const [drafts, setDrafts] = useState({});
    const [savingTemplate, setSavingTemplate] = useState("");
    const [sendingTest, setSendingTest] = useState("");

    // Activity log state.
    const [dispatches, setDispatches] = useState([]);
    const [dispatchTotal, setDispatchTotal] = useState(0);
    const [dispatchPage, setDispatchPage] = useState(1);
    const [dispatchLoading, setDispatchLoading] = useState(false);

    const loadSystem = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await getSystem();
            const data = res?.data || {};
            setRules(data.rules || []);
            setTemplates(data.templates || []);
            const nextConfigs = {};
            (data.rules || []).forEach((r) => {
                if (r.rule_type !== "proximity") return;
                const c = r.config && typeof r.config === "object" ? r.config : {};
                nextConfigs[r.slug] = {
                    radius_miles: c.radius_miles ?? 0.25,
                    freshness_minutes: c.freshness_minutes ?? 45,
                    max_per_week: c.max_per_week ?? 3,
                    min_hours_between: c.min_hours_between ?? 48
                };
            });
            setRuleConfigs(nextConfigs);
            const nextDrafts = {};
            (data.templates || []).forEach((t) => {
                nextDrafts[t.slug] = {
                    title: t.title || "",
                    body: t.body || "",
                    data: stringifyData(t.data),
                    enabled: Boolean(t.enabled)
                };
            });
            setDrafts(nextDrafts);
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "loadSystem" });
            setError(err?.message || "Failed to load the notification system.");
        } finally {
            setLoading(false);
        }
    }, [getSystem]);

    useEffect(() => {
        loadSystem();
    }, [loadSystem]);

    const loadDispatches = useCallback(
        async (page) => {
            setDispatchLoading(true);
            try {
                const res = await listDispatches({ page, pageSize: PAGE_SIZE });
                const data = res?.data || {};
                setDispatches(data.rows || []);
                setDispatchTotal(Number.isFinite(data.total) ? data.total : 0);
            } catch (err) {
                captureError(err, { view: "NotificationSystem", action: "loadDispatches" });
                setError(err?.message || "Failed to load the activity log.");
            } finally {
                setDispatchLoading(false);
            }
        },
        [listDispatches]
    );

    useEffect(() => {
        if (tab === 2) {
            loadDispatches(dispatchPage);
        }
    }, [tab, dispatchPage, loadDispatches]);

    // ── Rules ───────────────────────────────────────────────────────────────
    const handleRuleToggle = async (rule, enabled) => {
        setError("");
        try {
            const res = await updateRule({ slug: rule.slug, enabled });
            setRules((prev) => prev.map((r) => (r.slug === rule.slug ? res?.data?.rule || r : r)));
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "updateRule" });
            setError(err?.message || "Failed to update the rule.");
        }
    };

    const handleCooldownBlur = async (rule, value) => {
        const days = Number(value);
        if (!Number.isFinite(days) || days === rule.cooldown_days) return;
        try {
            const res = await updateRule({ slug: rule.slug, cooldown_days: days });
            setRules((prev) => prev.map((r) => (r.slug === rule.slug ? res?.data?.rule || r : r)));
            setToast("Cooldown updated.");
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "updateRuleCooldown" });
            setError(err?.message || "Failed to update the cooldown.");
        }
    };

    const setConfigField = (slug, key, value) => {
        setRuleConfigs((prev) => ({ ...prev, [slug]: { ...prev[slug], [key]: value } }));
    };

    const handleConfigBlur = async (rule, key, value) => {
        const num = Number(value);
        const current = rule.config && typeof rule.config === "object" ? rule.config : {};
        if (!Number.isFinite(num) || num === Number(current[key])) return;
        try {
            const res = await updateRule({ slug: rule.slug, config: { [key]: num } });
            const updated = res?.data?.rule;
            setRules((prev) => prev.map((r) => (r.slug === rule.slug ? updated || r : r)));
            if (updated && updated.config && typeof updated.config === "object") {
                setRuleConfigs((prev) => ({
                    ...prev,
                    [rule.slug]: { ...prev[rule.slug], ...updated.config }
                }));
            }
            setToast("Rule updated.");
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "updateRuleConfig" });
            setError(err?.message || "Failed to update the rule.");
        }
    };

    const handleProximityRun = async (dryRun) => {
        setRunningProximity(true);
        setError("");
        try {
            await triggerProximity({ dryRun });
            setToast(dryRun ? "Proximity dry run started." : "Proximity run started.");
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "triggerProximity" });
            setError(err?.message || "Failed to trigger the proximity run.");
        } finally {
            setRunningProximity(false);
        }
    };

    // ── Templates ───────────────────────────────────────────────────────────
    const setDraftField = (slug, field, value) => {
        setDrafts((prev) => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
    };

    const handleSaveTemplate = async (slug) => {
        const draft = drafts[slug];
        if (!draft) return;

        let parsedData;
        try {
            parsedData = draft.data && draft.data.trim() ? JSON.parse(draft.data) : {};
        } catch (_err) {
            setError(`Deep-link data for "${slug}" must be valid JSON.`);
            return;
        }
        if (parsedData === null || typeof parsedData !== "object" || Array.isArray(parsedData)) {
            setError(`Deep-link data for "${slug}" must be a JSON object.`);
            return;
        }

        setSavingTemplate(slug);
        setError("");
        try {
            const res = await updateTemplate({
                slug,
                title: draft.title,
                body: draft.body,
                data: parsedData,
                enabled: draft.enabled
            });
            const updated = res?.data?.template;
            if (updated) {
                setTemplates((prev) => prev.map((t) => (t.slug === slug ? updated : t)));
            }
            setToast("Template saved.");
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "updateTemplate" });
            setError(err?.message || "Failed to save the template.");
        } finally {
            setSavingTemplate("");
        }
    };

    const handleSendTest = async (slug) => {
        setSendingTest(slug);
        setError("");
        try {
            const res = await sendTest({ templateSlug: slug });
            const delivered = res?.data?.delivered ?? 0;
            setToast(
                delivered > 0
                    ? `Test pushed to ${delivered} of your device(s).`
                    : "Test recorded, but you have no registered devices."
            );
        } catch (err) {
            captureError(err, { view: "NotificationSystem", action: "sendTest" });
            setError(err?.message || "Failed to send the test notification.");
        } finally {
            setSendingTest("");
        }
    };

    const formatDate = (value) => {
        if (!value) return "";
        const parsed = value?.toDate ? dayjs(value.toDate()) : dayjs(value);
        return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : "";
    };

    const dispatchPageCount = Math.max(1, Math.ceil((dispatchTotal || 0) / PAGE_SIZE));

    if (loading) {
        return (
            <PageContainer>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Header>
                <NotificationsActiveRoundedIcon fontSize="large" />
                <div>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>
                        Notification System
                    </Typography>
                    <Subtle variant="body2">
                        Lifecycle push notifications for app users. Sending is gated by the flags in
                        System Config.
                    </Subtle>
                </div>
            </Header>

            {error ? (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            ) : null}

            <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1 }}>
                <Tab label="Rules" {...a11yProps(0)} />
                <Tab label="Templates" {...a11yProps(1)} />
                <Tab label="Activity" {...a11yProps(2)} />
            </Tabs>

            {/* ── Rules ────────────────────────────────────────────────────── */}
            <TabPanel value={tab} index={0}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Rules
                </Typography>
                <Stack spacing={2}>
                    {rules.map((rule) => {
                        const isProximity = rule.rule_type === "proximity";
                        const cfg = ruleConfigs[rule.slug] || {};
                        const perWeek = Math.max(Number(cfg.max_per_week) || 1, 1);
                        const recommendedMin = Math.ceil(168 / perWeek);
                        const clustered = Number(cfg.min_hours_between) < recommendedMin;
                        return (
                            <Paper key={rule.slug} variant="outlined" sx={{ p: 2 }}>
                                <Stack
                                    direction={isMobile ? "column" : "row"}
                                    spacing={2}
                                    alignItems={isMobile ? "flex-start" : "center"}
                                    justifyContent="space-between"
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontWeight: 700 }}>{rule.name}</Typography>
                                        <Subtle variant="body2">{rule.description}</Subtle>
                                    </Box>
                                    {!isProximity ? (
                                        <TextField
                                            label="Cooldown (days)"
                                            type="number"
                                            size="small"
                                            defaultValue={rule.cooldown_days}
                                            onBlur={(e) => handleCooldownBlur(rule, e.target.value)}
                                            sx={{ width: 140 }}
                                            helperText=" "
                                        />
                                    ) : null}
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(rule.enabled)}
                                                onChange={(e) =>
                                                    handleRuleToggle(rule, e.target.checked)
                                                }
                                            />
                                        }
                                        label={rule.enabled ? "Enabled" : "Disabled"}
                                    />
                                </Stack>

                                {isProximity ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Stack
                                            direction={isMobile ? "column" : "row"}
                                            spacing={2}
                                            flexWrap="wrap"
                                            useFlexGap
                                        >
                                            <TextField
                                                label="Radius (miles)"
                                                type="number"
                                                size="small"
                                                value={cfg.radius_miles ?? ""}
                                                onChange={(e) =>
                                                    setConfigField(
                                                        rule.slug,
                                                        "radius_miles",
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={(e) =>
                                                    handleConfigBlur(
                                                        rule,
                                                        "radius_miles",
                                                        e.target.value
                                                    )
                                                }
                                                inputProps={{ step: 0.05, min: 0.05, max: 25 }}
                                                helperText="Distance to a bar that counts as nearby"
                                                sx={{ width: 150 }}
                                            />
                                            <TextField
                                                label="Freshness (min)"
                                                type="number"
                                                size="small"
                                                value={cfg.freshness_minutes ?? ""}
                                                onChange={(e) =>
                                                    setConfigField(
                                                        rule.slug,
                                                        "freshness_minutes",
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={(e) =>
                                                    handleConfigBlur(
                                                        rule,
                                                        "freshness_minutes",
                                                        e.target.value
                                                    )
                                                }
                                                inputProps={{ step: 5, min: 5, max: 720 }}
                                                helperText="Max age of the user's last-known location"
                                                sx={{ width: 150 }}
                                            />
                                            <TextField
                                                label="Max / week"
                                                type="number"
                                                size="small"
                                                value={cfg.max_per_week ?? ""}
                                                onChange={(e) =>
                                                    setConfigField(
                                                        rule.slug,
                                                        "max_per_week",
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={(e) =>
                                                    handleConfigBlur(
                                                        rule,
                                                        "max_per_week",
                                                        e.target.value
                                                    )
                                                }
                                                inputProps={{ step: 1, min: 1, max: 21 }}
                                                helperText="Most nudges one user can get per week"
                                                sx={{ width: 150 }}
                                            />
                                            <TextField
                                                label="Min hours between"
                                                type="number"
                                                size="small"
                                                value={cfg.min_hours_between ?? ""}
                                                onChange={(e) =>
                                                    setConfigField(
                                                        rule.slug,
                                                        "min_hours_between",
                                                        e.target.value
                                                    )
                                                }
                                                onBlur={(e) =>
                                                    handleConfigBlur(
                                                        rule,
                                                        "min_hours_between",
                                                        e.target.value
                                                    )
                                                }
                                                error={clustered}
                                                inputProps={{ step: 1, min: 1, max: 336 }}
                                                helperText="Minimum gap between a user's nudges"
                                                sx={{ width: 170 }}
                                            />
                                        </Stack>
                                        <Alert
                                            severity={clustered ? "warning" : "info"}
                                            sx={{ mt: 2 }}
                                        >
                                            {clustered
                                                ? `At ${perWeek} nudges/week, only ${Number(
                                                      cfg.min_hours_between
                                                  )}h apart lets several land on back-to-back days followed by a long gap. Set "Min hours between" to at least ${recommendedMin}h to spread them evenly across the week.`
                                                : `Evenly spaced: ${perWeek} nudges/week at ${Number(
                                                      cfg.min_hours_between
                                                  )}h or more apart won't bunch onto back-to-back days.`}
                                        </Alert>
                                        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                disabled={runningProximity}
                                                onClick={() => handleProximityRun(true)}
                                            >
                                                Dry run
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                disabled={runningProximity}
                                                onClick={() => handleProximityRun(false)}
                                            >
                                                Run now
                                            </Button>
                                        </Stack>
                                    </Box>
                                ) : null}
                            </Paper>
                        );
                    })}
                </Stack>
            </TabPanel>

            {/* ── Templates ────────────────────────────────────────────────── */}
            <TabPanel value={tab} index={1}>
                <Stack spacing={3}>
                    {templates.map((template) => {
                        const draft = drafts[template.slug] || {};
                        return (
                            <Paper key={template.slug} variant="outlined" sx={{ p: 3 }}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 2 }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {template.name}
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(draft.enabled)}
                                                onChange={(e) =>
                                                    setDraftField(
                                                        template.slug,
                                                        "enabled",
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label="Active"
                                    />
                                </Stack>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Title"
                                        size="small"
                                        fullWidth
                                        value={draft.title || ""}
                                        onChange={(e) =>
                                            setDraftField(template.slug, "title", e.target.value)
                                        }
                                    />
                                    <TextField
                                        label="Body"
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        value={draft.body || ""}
                                        onChange={(e) =>
                                            setDraftField(template.slug, "body", e.target.value)
                                        }
                                        helperText="Variables: {{app_name}}."
                                    />
                                    <TextField
                                        label="Deep-link data (JSON)"
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        value={draft.data || ""}
                                        onChange={(e) =>
                                            setDraftField(template.slug, "data", e.target.value)
                                        }
                                        helperText='Extra push payload, e.g. {"type":"promotion_favorite","barId":"…"}.'
                                    />
                                    <Stack
                                        direction={isMobile ? "column" : "row"}
                                        spacing={2}
                                        alignItems={isMobile ? "stretch" : "center"}
                                        justifyContent="space-between"
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleSaveTemplate(template.slug)}
                                            disabled={savingTemplate === template.slug}
                                        >
                                            {savingTemplate === template.slug
                                                ? "Saving…"
                                                : "Save template"}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleSendTest(template.slug)}
                                            disabled={sendingTest === template.slug}
                                        >
                                            {sendingTest === template.slug
                                                ? "Sending…"
                                                : "Send test to me"}
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            </TabPanel>

            {/* ── Activity ─────────────────────────────────────────────────── */}
            <TabPanel value={tab} index={2}>
                <Paper variant="outlined">
                    {dispatchLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : dispatches.length === 0 ? (
                        <Box sx={{ p: 3 }}>
                            <Subtle variant="body2">No notification activity yet.</Subtle>
                        </Box>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Rule</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Status</TableCell>
                                    {!isMobile ? <TableCell>Detail</TableCell> : null}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dispatches.map((row) => (
                                    <TableRow key={row.id}>
                                        <TableCell>{formatDate(row.created_at)}</TableCell>
                                        <TableCell>
                                            {row.rule_slug || (row.is_test ? "test" : "—")}
                                        </TableCell>
                                        <TableCell>{row.user_id}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.status}
                                                color={statusColor(row.status)}
                                            />
                                        </TableCell>
                                        {!isMobile ? (
                                            <TableCell
                                                sx={{
                                                    maxWidth: 260,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis"
                                                }}
                                            >
                                                {row.error ||
                                                    `${row.title || ""}${
                                                        row.device_count
                                                            ? ` · ${row.device_count} device(s)`
                                                            : ""
                                                    }`}
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
                {dispatchPageCount > 1 ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination
                            count={dispatchPageCount}
                            page={dispatchPage}
                            onChange={(_e, value) => setDispatchPage(value)}
                        />
                    </Box>
                ) : null}
            </TabPanel>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={3000}
                onClose={() => setToast("")}
                message={toast}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            />
        </PageContainer>
    );
}
